<template>
  <div>
    <div class="d-flex">
      <div class="fill-half-width pr-2">
        <v-banner
          icon="mdi-account-multiple"
          single-line
        >
          <h2>Parties to be notified</h2>

          <template
            #actions
          >
            <div class="pr-4">
              <v-tooltip
                bottom
              >
                <template #activator="{on, attrs}">
                  <v-btn
                    icon
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    @click="_ => togglePartiesSelection()"
                  >
                    <v-icon>
                      {{ togglePartiesSelectionIcon }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ togglePartiesSelectionText }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-banner>

        <v-list two-line>
          <v-list-item-group
            v-model="selectedParties"
            active-class="success--text"
            multiple
          >
            <v-list-item
              v-for="party of availableParties"
              :key="party.id"
              class="px-6"
              :disabled="!!party.error"
              :value="party.id"
            >
              <template #default="{ active }">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="active"
                  />
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ party.name }}
                  </v-list-item-title>

                  <v-list-item-subtitle
                    v-if="party.error"
                  >
                    <div
                      class="text-caption red--text d-flex align-center"
                    >
                      <v-icon
                        color="red"
                        left
                        small
                      >
                        mdi-cellphone-message-off
                      </v-icon>
                      {{ party.error }}
                    </div>
                  </v-list-item-subtitle>

                  <v-list-item-subtitle
                    v-else
                    class="d-flex"
                  >
                    <div class="text-caption d-flex align-center">
                      <v-icon
                        left
                        small
                      >
                        mdi-cellphone-message
                      </v-icon>
                      {{ party.phone.phone_number }}
                    </div>
                    <div
                      class="text-caption d-flex align-center pl-4"
                    >
                      <v-icon
                        left
                        small
                      >
                        mdi-flag
                      </v-icon>
                      {{ party.language }}
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action
                  class="force-enable"
                >
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="_ => openPartyEdit(party.id)"
                      >
                        <v-icon>
                          mdi-account-edit-outline
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Edit Party</span>
                  </v-tooltip>
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>

      <v-divider vertical />

      <div class="fill-half-width pl-2">
        <v-banner
          icon="mdi-message-text"
          single-line
        >
          <h2>Notifications to be sent</h2>

          <template #actions>
            <div class="pr-4">
              <v-tooltip
                bottom
              >
                <template #activator="{on, attrs}">
                  <v-btn
                    icon
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    @click="_ => toggleNotificationsSelection()"
                  >
                    <v-icon>
                      {{ toggleNotificationsSelectionIcon }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ toggleNotificationsSelectionText }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-banner>

        <v-list two-line>
          <v-list-item-group
            v-model="selectedNotifications"
            active-class="success--text"
            multiple
          >
            <v-list-item
              v-for="notification of availableNotifications"
              :key="notification.id"
              class="px-6"
              :disabled="notification.disabled"
              :value="notification.id"
            >
              <template #default="{ active }">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="active"
                  />
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ notification.name }}
                  </v-list-item-title>

                  <v-list-item-subtitle
                    class="d-flex"
                  >
                    <div class="text-caption d-flex align-center">
                      <v-icon
                        left
                        small
                      >
                        mdi-calendar-clock
                      </v-icon>
                      {{ notification.date }}
                    </div>
                    <div class="text-caption d-flex align-center pl-4">
                      <v-icon
                        left
                        small
                      >
                        mdi-flag
                      </v-icon>
                      {{ notification.languages }}
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="_ => onPreviewNotificationTranslations(notification)"
                      >
                        <v-icon>mdi-comment-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>
    </div>

    <div
      v-if="!isValid"
      class="px-4 pt-8"
    >
      <v-alert
        prominent
        text
        type="error"
      >
        <span v-if="selectedParties.length">
          You must select at least a Notification to be sent or unselect any Parties to be notified
        </span>
        <span v-else>
          You must select at least a Party to send Notifications to or unselect any Notifications to be sent
        </span>
      </v-alert>
    </div>

    <div
      v-if="!requiredFieldsSet"
      class="overlay-parent d-flex justify-center"
    >
      <div class="pt-16">
        <v-alert
          border="left"
          max-width="600"
          outlined
          prominent
          text
          type="error"
        >
          In order to configure notifications the Date, Start Time, End Time and Timezone fields must be filled.
        </v-alert>
      </div>
    </div>

    <v-dialog
      v-model="isPreviewNotificationTranslationsDialogVisible"
      max-width="600"
      scrollable
    >
      <v-card>
        <v-card-title>Notification Translations Preview</v-card-title>
        <v-divider />
        <v-card-text class="pa-2">
          <v-card
            v-for="translation of previewNotificationTranslations"
            :key="translation.language"
            class="ma-4"
            outlined
          >
            <div class="px-4 text-overline">
              <v-icon
                left
                size="16"
              >
                mdi-flag
              </v-icon>
              <span
                class="font-weight-light"
              >
                {{ translation.language }}
              </span>
            </div>
            <v-divider />
            <div class="px-4 py-3">
              {{ translation.example }}
            </div>
          </v-card>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="_ => onClosePreviewNotificationTranslations()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { clone, cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'

import { partyName } from '@/utilities'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { PARTY_NAMESPACE } from '@/store/party'
import { RECORD_NAMESPACE } from '@/store/record'
import { SHARED_NAMESPACE } from '@/store/shared'
import { SHORT_MESSAGE_NAMESPACE } from '@/store/short-message'

const timeUnits = [
  {
    plural: 'days',
    singular: 'day',
    unit: 'days',
  },
  {
    plural: 'hours',
    singular: 'hour',
    unit: 'hours',
  },
]

export default {
  name: 'DocketEntryEditorNotifications',

  data () {
    return {
      demoParty: {
        first_name: 'John',
        last_name: 'Doe',
        middle_name: '',
      },

      isLoading: true,
      isPreviewNotificationTranslationsDialogVisible: false,
      previewNotificationId: null,
      previouslySelectedNotifications: [],
      previouslySelectedParties: [],
      record: null,
      requiresAttention: false,
      selectedNotifications: [],
      selectedParties: [],
    }
  },

  computed: {
    activeAttendees () {
      return this.docketEntry.attendances.reduce((res, att) => {
        if (this.activeParties.includes(att.party_id)) res.push(att.party_id)
        return res
      }, [])
    },

    activeNotifications () {
      return this.availableNotifications.reduce((res, an) => {
        if (!an.disabled) res.push(an.id)
        return res
      }, [])
    },

    activeParties () {
      return this.availableParties.reduce((res, cp) => {
        if (!cp.error) res.push(cp.id)
        return res
      }, [])
    },

    allNotificationsSelected () {
      return this.selectedNotifications.length === this.activeNotifications.length
    },

    allPartiesSelected () {
      return this.selectedParties.length === this.activeParties.length
    },

    allowedNotificationEvents () {
      if (this.isDocketEntryEditorInRescheduleMode) {
        return [
          'completed',
          'reminder',
          'rescheduled',
        ]
      }

      if (this.docketEntry.id) {
        return [
          'completed',
          'reminder',
        ]
      }

      return [
        'completed',
        'created',
        'reminder',
      ]
    },

    appointmentEndDateTime () {
      return `${this.docketEntry.action_date} ${this.docketEntry.event_time_end}`
    },

    appointmentStartDateTime () {
      return `${this.docketEntry.action_date} ${this.docketEntry.event_time_start}`
    },

    availableNotifications () {
      return this.docketEntryType?.docket_entry_type_notifications?.reduce((res, detn) => {
        if (!detn || !this.allowedNotificationEvents.includes(detn.event_type)) return res

        const template = this.shortMessageTemplates.
          find(smt => smt.id === detn.short_message_template_id)

        const languages = template.short_message_template_translations.
          reduce((res, smtt) => {
            const language = this.optionsLanguages?.find(ol => ol.id === smtt.language_id)?.name
            if (this.partyLanguages.includes(language)) res.push(language)
            return res
          }, []).
          sort((a, b) => {
            const x = (a.name || '').toUpperCase()
            const y = (b.name || '').toUpperCase()
            return ((x < y) ? -1 : ((x > y) ? 1 : 0))
          })

        const triggerDate = this.calculateNotificationTriggerDate(detn)

        if (!!triggerDate && triggerDate.isBefore()) return res

        res.push({
          date: triggerDate && triggerDate.isValid()
            ? moment.tz(triggerDate.format(), this.docketEntry.timezone).format('lll zz')
            : 'Immediately',
          disabled: false,
          id: detn.id,
          languages: languages.join(', '),
          name: template.title,
          trigger_at: triggerDate,
        })

        return res
      }, []) || []
    },

    availableParties () {
      const parties = (this.legalCaseParties || []).concat([
        { ...this.record?.record_holder_party, id: this.record?.record_holder_party_id },
        { ...this.record?.contact_party, id: this.record?.contact_party_id },
      ]).reduce((res, party) => {
        if (res.find(p => p.id === party.id)) return res

        const entry = {
          error: false,
          id: party.id,
          language: party.primary_language || party.learned_languages?.find(ll => ll.primary === true)?.language_string || 'English',
          name: partyName(party),
          phone: party.phones?.find(phone => phone.preferred_text === true),
        }

        if (!entry.phone?.phone_number) {
          entry.error = 'This Party has no preferred text phone number set!'
        } else if (entry.phone.phone_type === 'wa' && !this.twilioHasWhatsAppNumber) {
          entry.error = 'This party has a WhatsApp number set as preferred but WhatsApp messaging is disabled.'
        } else if (entry.phone.phone_type === 'inttext' && !this.twilioHasInternationalMessaging) {
          entry.error = 'This party has an international phone number but international messaging is disabled.'
        }

        res.push(entry)
        return res
      }, [])

      return parties.sort((a, b) => {
        // Parties with errors show last
        if (!a.error && !!b.error) return -1
        if (!b.error && !!a.error) return 1
        // sort by Party name
        const x = (a.name || '').toUpperCase()
        const y = (b.name || '').toUpperCase()
        return ((x < y) ? -1 : ((x > y) ? 1 : 0))
      })
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryType () {
      return this.docketEntryTypes.find(det => det.id === this.docketEntry.docket_entry_type_id)
    },

    englishLanguageId () {
      return this.optionsLanguages?.find(lang => lang.name === 'English')?.id
    },

    isValid () {
      return !(!this.selectedParties.length ^ !this.selectedNotifications.length)
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties || []
    },

    optionsLanguages () {
      return this.getCachedDropdown('languages')
    },

    partyLanguages () {
      const languages = this.availableParties.map(cp => cp.language)
      if (!languages.includes('English')) languages.push('English')
      return languages
    },

    previewNotificationTranslations () {
      if (!this.previewNotificationId || !this.docketEntryType?.docket_entry_type_notifications) return []

      const detn = this.docketEntryType.docket_entry_type_notifications.
        find(detn => detn.id === this.previewNotificationId)

      if (!detn) return []

      const template = this.shortMessageTemplates.
        find(smt => smt.id === detn.short_message_template_id)

      if (!template) return []

      const translations = template.short_message_template_translations.
        reduce((res, smtt) => {
          const language = this.optionsLanguages?.find(ol => ol.id === smtt.language_id)?.name
          if (!this.partyLanguages.includes(language)) return res

          res.push({
            example: this.generateConfirmationMessage(smtt.message, this.demoParty),
            language: language,
            template: smtt.message,
          })

          return res
        }, [])

      return translations
    },

    requiredFieldsSet () {
      return this.docketEntry.action_date && this.docketEntry.event_time_start && this.docketEntry.timezone
    },

    toggleNotificationsSelectionIcon () {
      if (this.allNotificationsSelected) return 'mdi-checkbox-multiple-blank-outline'
      return 'mdi-checkbox-multiple-marked-outline'
    },

    toggleNotificationsSelectionText () {
      if (this.allNotificationsSelected) return 'Unselect all'
      return 'Select all'
    },

    togglePartiesSelectionIcon () {
      if (this.allPartiesSelected) return 'mdi-checkbox-multiple-blank-outline'
      return 'mdi-checkbox-multiple-marked-outline'
    },

    togglePartiesSelectionText () {
      if (this.allPartiesSelected) return 'Unselect all'
      return 'Select all'
    },

    twilioHasInternationalMessaging () {
      return !!this.features?.twilio?.international_enabled
    },

    twilioHasWhatsAppNumber () {
      return !!this.features?.twilio?.has_whatsapp
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
      'isDocketEntryEditorInCloneMode',
      'isDocketEntryEditorInRescheduleMode',
      'originalDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
      'getCachedDropdown',
    ]),

    ...mapGetters(SHORT_MESSAGE_NAMESPACE, [
      'shortMessageTemplates',
    ]),
  },

  watch: {
    appointmentStartDateTime () {
      this.requiresAttention = !!this.activeNotifications.length
    },

    availableParties: {
      handler () {
        this.resetSelectedParties()
      },

      immediate: true,
    },

    docketEntryType: {
      handler () {
        this.resetSelectedNotifications()
        this.requiresAttention = !!this.activeNotifications.length
      },

      immediate: true,
    },

    isValid: {
      handler (value) {
        this.$emit('update:valid', value)
      },

      immediate: true,
    },

    legalCase: {
      handler (val) {
        if (!val) return
        if (val.record_id === this.record?.id) return

        this.getRecordById(val.record_id).then(data => { this.record = data })
      },

      immediate: true,
    },

    requiredFieldsSet: {
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        this.requiresAttention = !!this.activeNotifications.length
      },

      immediate: true,
    },

    requiresAttention: {
      handler (val) { this.$emit('update:warning', val) },
      immediate: true,
    },

    selectedNotifications () {
      this.buildDocketEntryNotificationsModel()
    },

    selectedParties () {
      this.buildDocketEntryNotificationsModel()
    },
  },

  async created () {
    this.isLoading = true

    await this.getFeatures()
    await this.getDropdownOptions({ options: false, path: 'languages' })

    this.isLoading = false
  },

  methods: {
    buildDocketEntryNotificationsModel () {
      const model = this.selectedParties.reduce((res, partyId) => {
        const entries = this.selectedNotifications.map(detnId => {
          const notification = this.availableNotifications.find(an => an.id === detnId)

          return {
            docket_entry_id: this.docketEntry.id,
            docket_entry_type_notification_id: detnId,
            id: null,
            party_id: partyId,
            trigger_at: notification.trigger_at || moment().format(),
          }
        })

        return res.concat(entries)
      }, [])

      if (this.docketEntry.id) {
        this.originalDocketEntry.docket_entry_notifications.forEach(den => {
          const entry = model.find(m => {
            return m.docket_entry_type_notification_id === den.docket_entry_type_notification_id &&
              m.party_id === den.party_id
          })

          if (entry) entry.id = den.id
          else model.push({ ...den, _destroy: true })
        })
      }

      this.docketEntry.docket_entry_notifications = model
    },

    calculateNotificationTriggerDate (notification) {
      if (!notification.interval || !notification.interval_unit) return ''

      if (!this.docketEntry.action_date || !this.docketEntry.event_time_start || !this.docketEntry.timezone) {
        return ''
      }

      const docketEntryActionDateTime = `${this.docketEntry.action_date} ${this.docketEntry.event_time_start}`

      return moment.
        tz(docketEntryActionDateTime, 'YYYY-MM-DD hh:mm a', this.docketEntry.timezone).
        subtract(notification.interval, notification.interval_unit)
    },

    generateConfirmationMessage (template, party) {
      const fields = [
        {
          name: 'DocketEntryType',
          value: this.docketEntryType.name,
        },
        {
          name: 'PartyName',
          value: partyName(party),
        },
        {
          name: 'ScheduledDate',
          value: moment.tz(this.docketEntry.action_date, this.docketEntry.timezone).format('ll'),
        },
        {
          name: 'ScheduledDateTime',
          value: moment.tz(`${this.docketEntry.action_date} ${this.docketEntry.event_time_start}`, this.docketEntry.timezone).
            format('llll zz'),
        },
        {
          name: 'ScheduledStartTime',
          value: this.docketEntry.event_time_start,
        },
        {
          name: 'ScheduledEndTime',
          value: this.docketEntry.event_time_end,
        },
        {
          name: 'Timezone',
          value: moment.tz(this.docketEntry.action_date, this.docketEntry.timezone).format('zz'),
        },
        {
          name: 'Location',
          value: this.docketEntry.appointment_location ? this.docketEntry.appointment_location : '',
        },
      ]

      return fields.reduce((text, field) => text.replace(`[${field.name}]`, field.value), template)
    },

    getUnitName (unit, isPlural) {
      const def = timeUnits.find(u => u.unit === unit)
      return isPlural ? def.plural : def.singular
    },

    onClosePreviewNotificationTranslations () {
      this.isPreviewNotificationTranslationsDialogVisible = false
    },

    onPreviewNotificationTranslations (notification) {
      this.previewNotificationId = notification.id
      this.isPreviewNotificationTranslationsDialogVisible = true
    },

    onTabOpened () {
      if (this.requiredFieldsSet) this.requiresAttention = false
    },

    openPartyEdit (partyId) {
      this.peSharedQESliderOpen(partyId)
    },

    partyName,

    resetSelectedNotifications () {
      if (this.docketEntry.id) {
        this.selectedNotifications = this.originalDocketEntry.
          docket_entry_notifications.reduce((res, den) => {
            if (!this.activeNotifications.includes(den.docket_entry_type_notification_id)) return res
            if (res.includes(den.docket_entry_type_notification_id)) return res

            res.push(den.docket_entry_type_notification_id)
            return res
          }, [])
      } else {
        this.selectedNotifications = clone(this.activeNotifications)
      }
    },

    resetSelectedParties () {
      if (this.originalDocketEntry.id && this.isDocketEntryEditorInRescheduleMode) {
        this.selectedParties = cloneDeep(this.activeAttendees)
      }
    },

    toggleNotificationsSelection () {
      this.selectedNotifications = this.allNotificationsSelected
        ? []
        : clone(this.activeNotifications)
    },

    togglePartiesSelection () {
      this.selectedParties = this.allPartiesSelected ? [] : clone(this.activeParties)
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peSharedQESliderOpen',
    ]),

    ...mapActions(RECORD_NAMESPACE, [
      'getRecordById',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
      'getFeatures',
    ]),
  },
}
</script>

<style scoped>
.fill-half-width {
  width: 100%;
  max-width: 50%;
}

.overlay-parent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
}

.v-list-item.v-list-item--disabled {
  .v-input--checkbox .v-icon {
    opacity: .5;
  }

  .force-enable {
    pointer-events: all;
  }
}
</style>
